import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import apiService from 'utils/apiService';

export const getProductMeta = async () => {
  const { data } = await apiService.get('/api/light/companyproducts/meta/');
  return data;
};

export const useProductMeta = () => {
  return useQuery(['product-meta'], () => getProductMeta(), {
    staleTime: 0,
  });
};

export const getProducts = async ({ pageParam = 1, ...q }) => {
  const { data } = await apiService.get('/api/light/companyproducts/', {
    ...q,
    page: pageParam,
  });
  return data;
};

export const useProducts = (q: Record<string, any> = {}) => {
  return useInfiniteQuery(
    ['products', q],
    ({ pageParam }) => getProducts({ pageParam, ...q }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.next_page ?? undefined;
      },
      staleTime: 30 * 60 * 1000,
    }
  );
};

export const useDesktopProducts = (q: Record<string, any> = {}, page = 1) => {
  return useQuery(
    ['products', 'desktop', q, page],
    () => getProducts({ pageParam: page, ...q }),
    {
      staleTime: 30 * 60 * 1000,
      keepPreviousData: true,
    }
  );
};

export const getProductDetails = async (id: string) => {
  const { data } = await apiService.get(
    `/api/light/companyproducts/${id}/details/`
  );
  return data;
};

export const useProductDetails = (id: string) => {
  return useQuery(
    ['product-details', id, 'imported'],
    () => getProductDetails(id),
    {
      staleTime: 60 * 60 * 1000,
    }
  );
};

export const deleteProduct = async (id: string) => {
  const { data } = await apiService.delete(`/api/light/companyproducts/${id}/`);
  return data;
};

export const activateProduct = async (id: string) => {
  const { data } = await apiService.post(
    `/api/light/companyproducts/${id}/active/`
  );
  return data;
};

export const inactiveProduct = async (id: string) => {
  const { data } = await apiService.post(
    `/api/light/companyproducts/${id}/inactive/`
  );
  return data;
};

const getProductFieldConfig = async (q: Record<string, any>) => {
  const { data } = await apiService.get(
    `/api/light/companyproducts/fields_config/`,
    q
  );
  return data;
};

export const useProductFieldConfig = (q: Record<string, any>) => {
  return useQuery(['product-field-config', q], () => getProductFieldConfig(q), {
    staleTime: q?.product ? 0 : Infinity,
  });
};

export const createProduct = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    '/api/light/companyproducts/',
    payload
  );
  return data;
};

export const updateProduct = async (
  productId: string,
  payload: Record<string, any>
) => {
  const { data } = await apiService.put(
    `/api/light/companyproducts/${productId}/`,
    payload
  );
  return data;
};

const getProductColorChoices = async (id: number) => {
  if (!id) return [];
  const { data } = await apiService.get(
    `/api/light/companyproducts/${id}/color_choices/`
  );
  return data;
};

export const useProductColorChoices = (id: number) => {
  return useQuery(
    ['product', `${id}`, 'color-choices'],
    () => getProductColorChoices(id),
    {
      staleTime: 1 * 60 * 1000,
    }
  );
};

export const addNewColor = async (id: number, payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/light/companyproducts/${id}/add_new_color/`,
    payload
  );
  return data;
};

export const searchProducts = async (search: string) => {
  const { data } = await apiService.post(`/api/light/companyproducts/search/`, {
    search,
  });
  return data;
};

export const useSearchProducts = (search: string, enabled = true) => {
  return useQuery(
    ['products', 'search', search],
    () => searchProducts(search),
    {
      staleTime: 1 * 60 * 1000,
      enabled,
    }
  );
};

export const importSupplierProduct = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/light/companyproducts/import_supplier_product/`,
    payload
  );
  return data;
};

export const bulkActivateProducts = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/light/companyproducts/bulk_active/`,
    payload
  );
  return data;
};

export const bulkDeactivateProducts = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/light/companyproducts/bulk_inactive/`,
    payload
  );
  return data;
};

export const deleteLaborCost = async (id: string) => {
  const { data } = await apiService.delete(`/api/light/labor-costs/${id}/`);
  return data;
};

export const createLaborCost = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(`/api/light/labor-costs/`, payload);
  return data;
};

export const updateLaborCost = async (
  id: string,
  payload: Record<string, any>
) => {
  const { data } = await apiService.put(
    `/api/light/labor-costs/${id}/`,
    payload
  );
  return data;
};

export const getProductBranchAvailability = async (
  id: string,
  locationId: number,
  jobAccountId: number
) => {
  const { data } = await apiService.get(
    `/api/light/companyproducts/${id}/variants_availability_for_branch/?supplier_location=${locationId}&job_account_number=${jobAccountId}`
  );
  return data;
};

export const useProductBranchAvailability = (
  id: string,
  locationId: number,
  jobAccountId: number
) => {
  return useQuery(
    [
      'product-branch-availability',
      id,
      locationId?.toString(),
      jobAccountId?.toString(),
    ],
    () => getProductBranchAvailability(id, locationId, jobAccountId),
    {
      staleTime: 10 * 60 * 1000,
      enabled: !!id && !!locationId && !!jobAccountId,
    }
  );
};

export const updateProductUnitMaps = async (
  id: string,
  payload: Record<string, any>
) => {
  const { data } = await apiService.put(
    `/api/light/companyproducts/${id}/unit_maps/`,
    payload
  );
  return data;
};

export const getProductCosts = async (id: string) => {
  const { data } = await apiService.get(
    `/api/light/companyproducts/${id}/product_costs/`
  );
  return data;
};

export const useProductCosts = (id: string) => {
  return useQuery(
    ['product-costs', id?.toString()],
    () => getProductCosts(id),
    {
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const createProductCost = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(`/api/productcosts/`, payload);
  return data;
};

export const updateProductCost = async (
  id: string,
  payload: Record<string, any>
) => {
  const { data } = await apiService.put(`/api/productcosts/${id}/`, payload);
  return data;
};

export const bulkDeleteProductCosts = async (payload: Record<string, any>) => {
  const { data } = await apiService.post(
    `/api/productcosts/bulk_delete/`,
    payload
  );
  return data;
};
